import {FancyPage} from '@wandb/weave/components/FancyPage';
import {JSONNaN} from '@wandb/weave/core';
import _ from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router';
import {useHistory} from 'react-router-dom';

import {Log} from '../components/Log';
import {
  MultiRunWorkspaceProps,
  MultiRunWorkspaceWithContext,
} from '../components/MultiRunWorkspace';
import NoMatch from '../components/NoMatch';
import RunFiles from '../components/RunFiles';
import AutoRefreshSettings from '../containers/AutoRefreshSettings';
import {
  useGroupPageQuery,
  useRunQuery,
  useRunsStateQueryQuery,
  useUpsertRunMutation,
} from '../generated/graphql';
import {useViewer} from '../state/viewer/hooks';
import {setDocumentDescription, setDocumentTitle} from '../util/document';
import * as FilterTypes from '../util/filterTypes';
import {AccessOption} from '../util/permissions';
import * as RunHelpers from '../util/runhelpers';
import * as Run from '../util/runs';
import * as RunTypes from '../util/runTypes';
import * as Urls from '../util/urls';
import {decodeURIComponentSafe} from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import NoAccessWrapper from './NoAccessPage/NoAccessWrapper';
import {ProjectPageContextProvider} from './ProjectPage/ProjectPageContext';
import RunArtifacts from './Run/RunArtifacts';
import RunOverview from './Run/RunOverview';
import {useBasePollInterval} from './Run/useBasePollInterval';
import {RunGroupOverview} from './RunGroupOverview';

const useRunSelection = (props: {
  entityName: string;
  projectName: string;
  groupName: string;
}) => {
  const {groupName} = props;
  const [selectedRunName, setSelectedRunName] = useState<string>();
  const firstRunQuery = useRunsStateQueryQuery({
    variables: {
      ...props,
      filters: JSON.stringify({group: groupName}),
      sampledHistorySpecs: [],
      groupKeys: [],
      groupLevel: 0,
      order: '-createdAt',
      limit: 1,
    },
  });
  const firstRunInGroup = _.first(
    firstRunQuery.data?.project?.runs?.edges.map(e => e.node)
  );

  useEffect(() => {
    if (selectedRunName == null && firstRunInGroup != null) {
      setSelectedRunName(firstRunInGroup.name);
    }
  }, [selectedRunName, firstRunInGroup]);

  return {selectedRunName, setSelectedRunName};
};

const useRunHelper = (
  projectName: string,
  entityName: string,
  runName?: string
) => {
  const {loading, data, refetch} = useRunQuery({
    skip: runName == null,
    variables: {entityName, projectName, runName: runName ?? ''},
  });
  const project = data?.project;
  const run = project?.run;
  const summaryMetrics =
    (run != null
      ? Run.parseSummary(run.summaryMetrics, run.name)
      : undefined) ?? {};

  const [updateRun] = useUpsertRunMutation();
  return {
    loading,
    run,
    project,
    summaryMetrics,
    updateRun,
    refetch,
  };
};

const useGroupPageSeo = (
  groupName: string,
  entityName: string,
  projectName: string,
  tab: string
) => {
  const tabDescription: string = useMemo(() => {
    switch (tab) {
      case `overview`:
        return `Overview`;
      case `system`:
        return `System`;
      case `logs`:
        return `Logs`;
      case `files`:
        return 'Files';
      case `artifacts`:
        return 'Artifacts';
      case `code`:
        return 'Code';
      case `table`:
        return `Table`;
      case `workspace`:
      default:
        return 'Workspace';
    }
  }, [tab]);

  const isInRunDetails = tab === 'runs';
  useEffect(() => {
    // run-specific document metadata gets set in RunDetails component
    if (!isInRunDetails) {
      setDocumentTitle(`${groupName} | ${projectName} ${tabDescription}`);
      setDocumentDescription(
        `${tabDescription} of group ${groupName} in ${projectName}, a machine learning project by ${entityName} using Weights & Biases.`
      );
    }
  }, [tabDescription, groupName, projectName, entityName, isInRunDetails]);
};

type GroupPageProps = {
  autoRefreshEnabled: boolean;
};

type GroupPageParams = {
  entityName: string;
  projectName: string;
  groupName: string;
  tab: string;
  filePath?: string;
};

const GroupPage = ({autoRefreshEnabled}: GroupPageProps) => {
  const viewer = useViewer();
  const history = useHistory();
  const {
    entityName,
    projectName,
    tab,
    filePath,
    groupName: encodedGroupName,
  } = useParams<GroupPageParams>();

  // Slightly confusing, but if `tab` URL param is "runs", we're actually looking
  // at a workspace in single-run mode. For the "Runs" sidebar item (i.e. expanded
  // runs table), the `tab` URL param value is actually "table".
  const activeSlug = tab === 'runs' ? 'workspace' : tab;

  // Group names can contain special characters, so we need to URL
  // decode the group name to recover the original name.
  const groupName = decodeURIComponentSafe(encodedGroupName);

  useGroupPageSeo(groupName, entityName, projectName, tab);

  const {loading, data} = useGroupPageQuery({
    variables: {entityName, projectName, groupName},
  });

  const {selectedRunName, setSelectedRunName} = useRunSelection({
    entityName,
    projectName,
    groupName,
  });

  const {run, project, summaryMetrics} = useRunHelper(
    projectName,
    entityName,
    selectedRunName
  );

  useBasePollInterval({
    runState: run?.state ?? undefined,
    runHistoryKeys: run?.historyKeys,
    autoRefreshEnabled,
  });

  if (loading) {
    return <Loader name="group-page-loader" />;
  }

  if (data?.project == null) {
    return <NoMatch />;
  }
  const {runGroup} = data.project;

  const isPublicOrOpenProject =
    project?.access === AccessOption.Public ||
    project?.access === AccessOption.Open;

  const mergeFilters: FilterTypes.Filter<RunTypes.Key> = {
    key: {section: 'run', name: 'group'},
    op: '=',
    value: groupName === '<null>' ? null : groupName,
  };

  const baseUrl = Urls.runGroup({
    entityName,
    projectName,
    name: groupName,
  });
  const onSetTableExpanded = (isTableExpanded: boolean) => {
    history.push({
      pathname: baseUrl + (isTableExpanded ? '/table' : '/workspace'),
      search: window.location.search,
    });
  };

  const workspaceMultiTabBody =
    (
      multiProps: Partial<MultiRunWorkspaceProps>,
      children?: () => React.ReactNode
    ) =>
    () =>
      selectedRunName == null && children != null ? (
        <Loader name="workspace-multi-tab-loader" />
      ) : (
        <MultiRunWorkspaceWithContext
          entityName={entityName}
          isTableExpanded={false}
          mergeFilters={mergeFilters}
          onSelectRunName={setSelectedRunName}
          onSetTableExpanded={onSetTableExpanded}
          projectName={projectName}
          recommendGrouping="jobtype"
          selectedRunName={children != null ? selectedRunName : undefined}
          title={`Group: ${groupName}`}
          viewType="group-view"
          {...multiProps}>
          {children?.()}
        </MultiRunWorkspaceWithContext>
      );

  return (
    <div className="group-page">
      <NoAccessWrapper skipCheck={isPublicOrOpenProject}>
        <ProjectPageContextProvider
          project={{
            id: data.project.id,
            name: projectName,
            entityName: entityName,
          }}>
          <FancyPage
            activeSlug={activeSlug ?? 'overview'}
            baseUrl={baseUrl}
            items={[
              {
                iconName: 'info',
                name: 'Group',
                slug: 'overview',
                render: workspaceMultiTabBody({isSingleMode: true}, () => (
                  <>
                    {runGroup && (
                      <RunGroupOverview
                        group={runGroup}
                        availableTags={project?.tags ?? []}
                        projectName={projectName}
                        entityName={entityName}
                        history={history}
                      />
                    )}
                    {run != null && (
                      <RunOverview
                        key={run.name}
                        entityName={entityName}
                        projectName={projectName}
                        runName={run.name}
                        project={project as any}
                        run={run as any}
                        summaryMetrics={summaryMetrics}
                        history={history}
                        config={JSONNaN.JSONparseNaN(run.config)}
                        viewer={viewer as any}
                      />
                    )}
                  </>
                )),
              },
              {
                iconName: 'dashboard-blackboard',
                name: 'Workspace',
                slug: 'workspace',
                render: workspaceMultiTabBody({isTableExpanded: false}),
              },
              {
                iconName: 'table',
                name: 'Runs',
                slug: 'table',
                render: workspaceMultiTabBody({isTableExpanded: true}),
              },
              {
                iconName: 'list',
                name: 'Logs',
                slug: 'logs',
                render: workspaceMultiTabBody(
                  {isSingleMode: true, showLogCounts: true},
                  () => {
                    if (run == null) {
                      return;
                    }
                    const outputLogFile = RunHelpers.outputLogFile(
                      run.outputLogFile
                    );
                    return (
                      <GroupPageLogsTab
                        entityName={entityName}
                        projectName={projectName}
                        runName={run.name}
                        outputLogFile={outputLogFile}
                      />
                    );
                  }
                ),
              },
              {
                iconName: 'document',
                name: `Files`,
                slug: 'files',
                render: workspaceMultiTabBody(
                  {isSingleMode: true},
                  () =>
                    run && (
                      <RunFiles
                        entityName={entityName}
                        projectName={projectName}
                        runName={run.name}
                        path={filePath}
                        history={history}
                        setSelectedRunName={setSelectedRunName}
                      />
                    )
                ),
              },
              {
                iconName: 'versions-layers',
                name: `Artifacts`,
                slug: 'artifacts',
                render: workspaceMultiTabBody(
                  {isSingleMode: true, showArtifactCounts: true},
                  () =>
                    run && (
                      <RunArtifacts
                        entityName={entityName}
                        projectName={projectName}
                        runName={run.name}
                        groupName={groupName}
                        sweepName={run.sweep?.name}
                      />
                    )
                ),
              },
            ]}
          />
        </ProjectPageContextProvider>
      </NoAccessWrapper>
    </div>
  );
};

const PollingGroupPage = () => {
  return (
    <AutoRefreshSettings>
      {autoRefreshEnabled => (
        <GroupPage autoRefreshEnabled={autoRefreshEnabled} />
      )}
    </AutoRefreshSettings>
  );
};

export default PollingGroupPage;

type GroupPageLogsTabProps = {
  entityName: string;
  projectName: string;
  runName: string;
  outputLogFile: string | null;
};

const GroupPageLogsTab: React.FC<GroupPageLogsTabProps> = React.memo(
  ({entityName, projectName, runName, outputLogFile}) => {
    return (
      <Log
        entityName={entityName}
        projectName={projectName}
        runName={runName}
        pollInterval={0}
        outputLogFile={outputLogFile}
        analyticsLocation="group-logs"
      />
    );
  }
);
